/* The single seam between v2 and the FastAPI backend. Every /api/* call goes
   through apiFetch — nothing in v2 should ever call fetch() directly, or the
   ?as= impersonation param silently stops propagating on that call.

   v1 achieves the same thing by monkey-patching window.fetch (app/static/app.js);
   doing it here keeps the side effect out of the global scope.

   This is also where `reel` becomes `item` (ADR 0002 §6). The rename is only a
   vocabulary one today — the server's summary shape already matches `Item` field
   for field — so the mapping is a type assertion rather than a transform. It
   still belongs here: when the shape does diverge, there is one place to change,
   and no component above this line has ever said "reel". */

import type {
  AuthConfig, Category, Facets, Item, ItemDetail, Me, Mention, Meta,
  ProposedCategory, SearchItem, Venue,
} from './types.js';

/**
 * Admin impersonation, read once at load. Server side is current_user() in
 * app/main.py.
 *
 * Exported because it is half of the browser's identity: while impersonating,
 * everything on screen — and everything cached — belongs to `AS`, not to the
 * signed-in account. `core/cache.ts` keys on it for exactly that reason.
 */
export const AS = new URLSearchParams(location.search).get('as');

/**
 * Absolute URL for an API path, carrying `?as=` when impersonating.
 *
 * Exported because two things legitimately need a URL rather than a response:
 * `<video src>` and `<img src>` are fetched by the browser, not by us, and would
 * silently drop the param if a caller built the string itself.
 */
export function apiUrl(path: string): string {
  const url = new URL(path, location.origin);
  if (AS) url.searchParams.set('as', AS);
  return url.toString();
}

/**
 * Returned instead of throwing when the session cookie is missing or expired.
 *
 * Typed `unique symbol` so `x !== NOT_SIGNED_IN` narrows at call sites; a plain
 * `symbol` doesn't, and every consumer would need a cast.
 */
export const NOT_SIGNED_IN: unique symbol = Symbol('not-signed-in');
export type MaybeSignedIn<T> = T | typeof NOT_SIGNED_IN;

/** Type guard for the cases where a `!==` check reads worse than a predicate. */
export const isSignedIn = <T>(v: MaybeSignedIn<T>): v is T => v !== NOT_SIGNED_IN;

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * GET/POST a JSON API path. Resolves to NOT_SIGNED_IN on 401 rather than
 * throwing — a 401 is a routing fact (show the sign-in view), not an error, and
 * every read in the app has to handle it.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<MaybeSignedIn<T>> {
  const resp = await fetch(apiUrl(path), { credentials: 'same-origin', ...init });
  if (resp.status === 401) return NOT_SIGNED_IN;
  if (!resp.ok) throw new ApiError(resp.status, await errorMessage(resp, path, init));
  return (await resp.json()) as T;
}

/**
 * FastAPI's `detail` when there is one, the bare status otherwise.
 *
 * Worth the extra read: the server's own wording is the only version a user can
 * act on ("No instagram.com/reel/... URL in the shared content" against a share
 * of an ordinary post), and `POST /api/share -> 400` is not.
 */
async function errorMessage(resp: Response, path: string, init?: RequestInit): Promise<string> {
  const fallback = `${init?.method ?? 'GET'} ${path} -> ${resp.status}`;
  try {
    const body = (await resp.json()) as { detail?: unknown };
    return typeof body?.detail === 'string' && body.detail ? body.detail : fallback;
  } catch {
    return fallback; // not JSON, or no body at all
  }
}

export const getMe = () => apiFetch<Me>('/api/me');
export const getMeta = () => apiFetch<Meta>('/api/meta');

/** What an unreachable or unconfigured /api/auth/config resolves to. */
const AUTH_OFF: AuthConfig = {
  enabled: false, apiKey: '', authDomain: '', projectId: '', appId: '',
};

/**
 * The Firebase web config, for the sign-in view.
 *
 * Swallows failures and resolves to a disabled config rather than handing back a
 * union nobody wants to narrow: an unreachable config endpoint and a server with
 * no auth configured mean the same thing to every caller — no sign-in here.
 *
 * Not memoised. It is five short strings, fetched at most twice in a session,
 * and caching it would mean caching a transient failure as "auth is off".
 */
export async function getAuthConfig(): Promise<AuthConfig> {
  try {
    const cfg = await apiFetch<AuthConfig>('/api/auth/config');
    return isSignedIn(cfg) ? cfg : AUTH_OFF;
  } catch {
    return AUTH_OFF;
  }
}

/**
 * Whether server-side auth is configured at all.
 *
 * Matters because local dev and pytest run fully open as the owner, where a
 * "Sign out" button would do nothing.
 */
export const authEnabled = async (): Promise<boolean> => (await getAuthConfig()).enabled;

/**
 * Exchange a Firebase ID token for our own session cookie.
 *
 * Returns the same payload as /api/me, so signing in costs no second round trip.
 * Throws `ApiError(403)` when the server refuses the account — an unverified
 * Google email, or a non-invited one while OPEN_SIGNUP is off. The one failure
 * the sign-in view words differently (it shows the server's `detail`), and the
 * reason this doesn't collapse every non-200 into a boolean.
 */
export async function createSession(idToken: string): Promise<Me> {
  const me = await apiFetch<Me>('/api/auth/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  // 401 here means Firebase issued a token our server wouldn't verify, which is
  // a real failure rather than "not signed in yet" — don't hand back the sentinel.
  if (!isSignedIn(me)) throw new ApiError(401, 'POST /api/auth/session -> 401');
  return me;
}

/**
 * In-flight import count for the header indicator.
 *
 * Deliberately `pending + processing` only. v1 excludes `failed` on purpose
 * (app/static/app.js:172) — failed imports are not in flight and belong on a
 * Settings surface, not a live counter.
 */
export function inFlightCount(meta: Meta): number {
  return (meta.queue.pending ?? 0) + (meta.queue.processing ?? 0);
}

/** Failed imports. Shown as a count only — there is no list/retry endpoint yet. */
export function failedCount(meta: Meta): number {
  return meta.queue.failed ?? 0;
}

// ── items ────────────────────────────────────────────────────

/** Browse page size. The server caps `limit` at 120; 60 matches v1. */
export const PAGE_SIZE = 60;

/** Search page size — the server's own `SEARCH_PAGE`, which it applies itself. */
export const SEARCH_PAGE_SIZE = 40;

/**
 * One page of the caller's library, newest-saved first.
 *
 * `category` is the caller's own bucketing, matched exactly — pass '' for all.
 */
export const getItems = (category: string, offset: number) =>
  apiFetch<Item[]>(
    `/api/reels?category=${encodeURIComponent(category)}&limit=${PAGE_SIZE}&offset=${offset}`,
  );

/**
 * One page of full-text search across transcripts, summaries, captions and tags,
 * ranked by FTS relevance and optionally narrowed to one category.
 *
 * Paging is safe here: the server orders by FTS rank, which is deterministic, so
 * pages don't reshuffle underneath the reader.
 */
export const searchItems = (q: string, category: string, offset: number) =>
  // SearchItem, not Item: query._augment attaches `cities` and `mentions` to
  // every search row and `snippet` whenever there was a match to excerpt. The
  // hand-written types missed all three until packages/api-types was generated.
  apiFetch<SearchItem[]>(
    `/api/search?q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}&offset=${offset}`,
  );

/**
 * How a query's matches distribute across the caller's categories.
 *
 * A separate endpoint rather than a field on the search response, deliberately:
 * v1 consumes `/api/search` too, and reshaping that payload would break the
 * shipping UI. Counts cover the whole result set, not the page in hand, so the
 * chips stay honest while paging.
 */
export const getSearchFacets = (q: string) =>
  apiFetch<Facets>(`/api/search/facets?q=${encodeURIComponent(q)}`);

/** Everything the analysis extracted for one item. Backs Detail. */
export const getItemDetail = (id: number) => apiFetch<ItemDetail>(`/api/reel/${id}`);

/**
 * Remove an item from the caller's library.
 *
 * `hard_deleted` reports whether the shared row went with it — it only does when
 * nobody else has the same URL saved. Not surfaced in the UI: from where the
 * user stands the item is gone either way, and "also deleted for everyone" is a
 * fact about other people's libraries that they can't act on (§23).
 */
export const deleteItem = (id: number) =>
  apiFetch<{ ok: boolean; hard_deleted: boolean }>(`/api/reel/${id}`, { method: 'DELETE' });

// ── video ────────────────────────────────────────────────────
// Video is never stored: /api/video/{shortcode} resolves Instagram's signed CDN
// URL on demand and either 307s to it or, with ?resolve=1, hands back JSON so the
// client can inspect `audio_missing` before committing it to <video src>.

/** Why a video won't play. `''` is "unclassified" — see `resolveVideo`. */
export type VideoFailure = 'restricted' | 'deleted' | 'rate_limited' | 'dash_only' | '';

/**
 * What the server knows about this reel's sound.
 *
 * `silent` is a fact about the reel — it has no audio track and never had one,
 * so the mute control is pointless and says so. `unavailable` is a fact about
 * *this resolve*: Instagram serves the container's datacenter IP formats with
 * the audio stripped, so the sound exists but didn't reach us. Telling them
 * apart needs the server's recorded `has_audio`; before it existed both looked
 * permanent.
 *
 * Both are now statements rather than offers. `unavailable` used to carry a
 * retry, on the reasoning that a soundless resolve was this attempt's bad luck.
 * It is not: the withholding is per reel and deterministic, so the same egress
 * returns the same answer every time (ADR 0042). What actually changes the
 * answer is asking from somewhere else.
 */
export type AudioState = 'present' | 'silent' | 'unavailable';

export type VideoOutcome =
  | { ok: true; url: string; audioMissing: boolean; audio: AudioState }
  | { ok: false; reason: VideoFailure };

const CLASSIFIED: readonly string[] = ['restricted', 'deleted', 'rate_limited'];

/**
 * Ask the server for a playable URL, or for the reason there isn't one.
 *
 * The distinction that matters is between a *classified* failure and an
 * unclassified one, and it is the whole reason this returns `''` rather than
 * throwing. A classified reason is permanent for this item and worth telling the
 * user about; anything else — a network blip, an unexpected status — is very
 * often transient, and v1 learned to retry it once before showing an error
 * rather than flashing a scary message at a CDN hiccup.
 *
 * `409` is the exception that is unclassified in the body but permanent in fact:
 * Instagram is serving this one as split DASH streams the in-page player cannot
 * combine, so it is mapped to `dash_only` here.
 */
export async function resolveVideo(shortcode: string): Promise<VideoOutcome> {
  try {
    /* No `refresh` here any more. It existed to drop the server's cached CDN
       URL for the audio retry — a degraded resolve is cached like any other, so
       a retry inside the cache window would re-serve the same silent format.
       The retry itself is gone (ADR 0042): re-resolving through the same egress
       returns the same answer, measured 15/15, because the withholding is a
       property of the reel and the address, not of the attempt. The server
       still accepts `?refresh=1`; nothing in the web client needs it. */
    const resp = await fetch(apiUrl(`/api/video/${encodeURIComponent(shortcode)}?resolve=1`), {
      credentials: 'same-origin',
    });
    if (resp.ok) {
      const info = (await resp.json()) as {
        url: string;
        audio_missing?: boolean;
        audio?: AudioState;
      };
      return {
        ok: true,
        url: info.url,
        audioMissing: Boolean(info.audio_missing),
        audio: info.audio ?? (info.audio_missing ? 'unavailable' : 'present'),
      };
    }
    if (resp.status === 409) return { ok: false, reason: 'dash_only' };
    const body = (await resp.json().catch(() => ({}))) as { detail?: string };
    const detail = body.detail ?? '';
    return { ok: false, reason: CLASSIFIED.includes(detail) ? (detail as VideoFailure) : '' };
  } catch {
    return { ok: false, reason: '' };
  }
}

/**
 * The redirecting video endpoint, for `<video src>` directly.
 *
 * The fallback path when `resolveVideo` comes back unclassified: let the element
 * follow the 307 itself and report through its own `error` event. `bust` defeats
 * the media cache on a retry — the same URL is otherwise re-served from it,
 * including the failure.
 */
export const videoUrl = (shortcode: string, bust?: number): string =>
  apiUrl(`/api/video/${encodeURIComponent(shortcode)}${bust ? `?r=${bust}` : ''}`);

/** Pre-resolve upcoming items server-side so moving through a playlist is instant. */
export function warmVideo(shortcode: string): void {
  void fetch(videoUrl(shortcode), { credentials: 'same-origin', redirect: 'manual' }).catch(
    () => {},
  );
}

// ── categories ───────────────────────────────────────────────
// Full CRUD already exists server-side; 'Other' is protected and is the default
// reassignment target on delete.

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const getCategories = () => apiFetch<Category[]>('/api/categories');

export const createCategory = (name: string, description: string) =>
  apiFetch<Category>('/api/categories', json('POST', { name, description }));

export const updateCategory = (id: number, patch: { name?: string; description?: string }) =>
  apiFetch<Category>(`/api/categories/${id}`, json('PATCH', patch));

/** Reassigns this user's items to `reassignTo`, or to 'Other' when omitted. */
export const deleteCategory = (id: number, reassignTo?: number) => {
  const q = reassignTo === undefined ? '' : `?reassign_to=${reassignTo}`;
  return apiFetch<{ ok: boolean; moved: number }>(`/api/categories/${id}${q}`, {
    method: 'DELETE',
  });
};

// ── first-run onboarding ─────────────────────────────────────
// The wizard replaces the shell until Me.onboarded flips. `propose` writes
// nothing — `complete` is the only call that touches the taxonomy, so a bad
// suggestion costs a tap.

export const getOnboardingSuggestions = () =>
  apiFetch<{ suggestions: string[]; max_length: number }>('/api/onboarding/suggestions');

/**
 * Ask for a proposed taxonomy. Server-side this is rate-limited for life and
 * 403s once onboarding is complete (app/pipeline/onboard.py explains why), so
 * treat a failure as terminal for the session rather than something to retry in
 * a loop.
 *
 * `quota_exhausted` comes back with an empty list rather than an error status —
 * the wizard shows a notice and offers to continue on the defaults.
 */
export const proposeCategories = (interests: string) =>
  apiFetch<{ categories: ProposedCategory[]; quota_exhausted: boolean }>(
    '/api/onboarding/propose',
    json('POST', { interests }),
  );

/** Finish onboarding. `categories: null` is the Skip path — keeps the defaults. */
export const completeOnboarding = (interests: string, categories: ProposedCategory[] | null) =>
  apiFetch<{ ok: boolean; moved: number }>(
    '/api/onboarding/complete',
    json('POST', { interests, categories }),
  );

export const logout = () => apiFetch<unknown>('/api/auth/logout', { method: 'POST' });

// ── places / map ─────────────────────────────────────────────
// The whole venue set arrives in one payload (521 rows with their items nested)
// and Map filters it client-side — by kind, by search text, and by whatever the
// camera is looking at. That last one is the reason there are no query params
// here even though `query.list_places` supports kind/city/country: a filter that
// re-queries on every pan would put the network in the middle of a drag.

export const getPlaces = () => apiFetch<Venue[]>('/api/places');

/** Per-user flag on a shared venue; other people's maps are unaffected. */
export const setVisited = (mapPlaceId: number, visited: boolean) =>
  apiFetch<{ ok: boolean }>(
    `/api/place/visited?map_place_id=${mapPlaceId}&visited=${visited}`,
    { method: 'POST' },
  );

/**
 * Hides a venue from this user's map, permanently. Read-side only: the shared
 * `map_places` row survives because the same venue sits on other users' maps.
 * There is no undo endpoint, which is why the caller confirms first.
 */
export const deletePlace = (mapPlaceId: number) =>
  apiFetch<{ ok: boolean }>(`/api/place?map_place_id=${mapPlaceId}`, { method: 'DELETE' });

// ── index / mentions ─────────────────────────────────────────
// The whole aggregate arrives in one payload (~1.3k rows) and Index filters it
// client-side, which is why there is no query param here: /api/mentions takes
// none, and searching over `detail` needs no backend or FTS work.

export const getMentions = () => apiFetch<Mention[]>('/api/mentions');

/** Identifies a row by value — `(name, kind)` is the aggregate's only key. */
const mentionQuery = (name: string, kind: string) =>
  `?name=${encodeURIComponent(name)}&kind=${encodeURIComponent(kind)}`;

/** Hides the pair from this user's Index, permanently. Still listed on the
 *  items that mention it. The path keeps its old name (ADR 0051). */
export const hideMention = (name: string, kind: string) =>
  apiFetch<{ ok: boolean }>(`/api/mentions/archive${mentionQuery(name, kind)}`, {
    method: 'POST',
  });
